import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StepperSelectionEvent, STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Plan } from '../common/models/plan';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Competency } from '../common/models/competency';
import { CompetencyTemplate } from '../common/models/competencyTemplate';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { StepStatus } from '../common/models/stepStatus';
import { StepStatusService } from '../common/services/step-status.service';
import { User } from '../common/models/user';
import { PlanService } from '../common/services/plan.service';
import { CompetencyService } from '../common/services/competency.service';
import { SetLearningComponent } from '../steps/set-learning/set-learning.component';
import { Activity } from '../common/models/activity';
import { Router } from '@angular/router';
import { LoaderService } from '../common/services/loader.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogSubmitPlanComponent } from '../dialogs/dialog-submit-plan/dialog-submit-plan.component';
import { SelfAssessmentComponent } from '../steps/self-assessment/self-assessment.component';
import { SelectCompetenciesComponent } from '../steps/select-competencies/select-competencies.component';
import { SharedService } from '../common/services/shared.service';
import { PlanEditService } from '../common/services/plan-edit.service';
import { UserService } from '../common/services/user.service';
import { SetPrioritiesComponent } from '../steps/set-priorities/set-priorities.component';

@Component({
    selector: 'app-self-assessment-wizard',
    templateUrl: './self-assessment-wizard.html',
    styleUrls: ['./self-assessment-wizard.css'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { displayDefaultIndicatorType: false },
        }
    ],
    standalone: false
})

export class SelfAssessmentWizardComponent implements OnInit {

  @ViewChild(SetLearningComponent) setLearning: SetLearningComponent;
  @ViewChild(SelfAssessmentComponent) selfAssessment: SelfAssessmentComponent;
  @ViewChild(SelectCompetenciesComponent) selectCompetencies: SelectCompetenciesComponent;
  @ViewChild(SetPrioritiesComponent) setPriorities: SetPrioritiesComponent;
  @ViewChild('stepper') stepper: MatStepper;

  @HostListener("window:beforeunload")
  canDeactivate(): boolean | Observable<boolean> {
    return !this.planEditService.getIsEditing();
  }

  public plan: Plan;
  public user: User;
  public stepStatus: StepStatus;
  public competencyList: CompetencyTemplate[];
  public deletedActivities: Activity[] = [];
  public savedCompetencies: Competency[];

  public landingAnnouncementString = '';

  public mouseOverTooltip: string;

  isButtonLoading = false;

  stepperOrientation: Observable<StepperOrientation>;

  isEditing = this.planEditService.getIsEditing()

  disableSetLearningButtons = false;

  constructor(breakpointObserver: BreakpointObserver, private _snackBar: MatSnackBar, private stepStatusService: StepStatusService, private planService: PlanService, private competencyService: CompetencyService, private router: Router, private loaderService: LoaderService, private dialog: MatDialog, private sharedService: SharedService, private planEditService: PlanEditService, private userService: UserService) {

    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }

  ngOnInit(): void {

    if (this.planEditService.getIsEditing()) {
      this.userService.user().subscribe(user => {
        this.user = user
        if (user)
        this.planService.getEditablePlan(user.Id, this.planEditService.getEditingPlanYear()).subscribe(p => {
          this.plan = p
          if (p) {
            this.stepStatusService.getStep(user.Id, p.Id).subscribe(step => {
              step.Step = this.planEditService.getEditorStartStep();
              this.stepStatus = step;
              this.pageLandingAnnouncement(step.Step);
            });
            if (!this.plan.Competencies)
              this.plan.Competencies = [];

            this.competencyService.getEditableCompetenciesByUserId(user.Id).subscribe({next: (savedCompetenices) => {
              this.savedCompetencies = savedCompetenices;
            }});
          }
        });
      });
    } else {
      this.userService.user().subscribe(user => {
        this.user = user
        if (user) {
          this.planService.getOrCreatePlan(user.Id).subscribe(p => {
            this.plan = p
            if (p) {
              this.stepStatusService.getStep(user.Id, p.Id).subscribe(step => {
                this.stepStatus = step;
                this.pageLandingAnnouncement(step.Step);
              });
              if (!this.plan.Competencies)
                this.plan.Competencies = [];

              this.competencyService.getCompetenciesByUserId(user.Id).subscribe({next: (savedCompetenices) => {
                this.savedCompetencies = savedCompetenices;
              }});
            }

          });
        }
      });
    }

  }

  disableButtons(disable: boolean){
    this.disableSetLearningButtons = disable;
  }

  savePage(step: number): void {
    this.saveStep(step)
  }

  markAsDirty($event: boolean) {
    this.planEditService.setIsDirty($event);
  }

  openSnackbarError(message: string) {
    this._snackBar.openFromComponent(SnackbarComponent, {
      data: message, panelClass: 'snackbar-error', duration: 5000
    });
  }

  saveCompetenciesPage() {
    if (!this.selectCompetencies.validateForm())
      return;
    this.isButtonLoading = true;
    const scrubbedCompetencies = this.competencyService.scrubCompetencies(this.plan.Competencies);
    this.competencyService.submitCompetencies(this.plan.Id, scrubbedCompetencies).subscribe({
      next: (r) => {
        this.plan.Competencies = [...r];
        this.completeSave();
        this.step();
      },
      error: () => {
        this.isButtonLoading = false;
        this.openSnackbarError("Unable to save competencies");
      }
    })
  }

  saveSelfAssessmentPage() {
    if (!this.selfAssessment.validateForm()) {
      return;
    }
    this.isButtonLoading = true;
    const scrubbedCompetencies = this.competencyService.scrubCompetencies(this.plan.Competencies);
    this.competencyService.submitCompetencyRating(this.plan.Id, scrubbedCompetencies).subscribe({
      next: (r) => {
        this.plan.Competencies = [...r];
        this.completeSave();
        this.step();
      },
      error: () => {
        this.isButtonLoading = false;
        this.openSnackbarError("Unable to save self assessment ratings");
      }
    })
  }

  saveStep(step?: number) {
    if (step && this.stepStatus) {
      this.stepStatus.Step = step;
    }
    this.step();
    this.completeSave();
  }

  completeSave() {
    this.planEditService.setIsDirty(false);
  }

  saveSetPrioritiesPage() {
    this.isButtonLoading = true;
    const scrubbedCompetencies = this.competencyService.scrubCompetencies(this.plan.Competencies);
    this.competencyService.submitCompetencyPriority(this.plan.Id, scrubbedCompetencies).subscribe({
      next: (r) => {
        this.plan.Competencies = [...r];
        this.completeSave();
        this.step();
      },
      error: () => {
        this.isButtonLoading = false;
        this.openSnackbarError("Unable to save priority order.");
      }
    })
  }

  saveSetLearning() {
    if (this.setLearning.validateForm()) {
      this.isButtonLoading = true;
      const scrubbedCompetencies = this.competencyService.scrubCompetencies(this.plan.Competencies);
      this.competencyService.submitCompetencyLearning(this.plan.Id, scrubbedCompetencies).subscribe({
        next: (r) => {
          this.plan.Competencies = [...r];
          this.completeSave();
          this.step();
        },
        error: () => {
          this.isButtonLoading = false;
          this.openSnackbarError("Unable to save learning activities.");
        }
      })
    } else{
      document.getElementsByTagName('mat-sidenav-content')[0]?.scrollTo(0, 0);
    }
  }

  submitPlan() {
    if (this.planEditService.getIsEditing()) {
      this.submitEditPlan();
    } else {
      this.submitNewPlan();
    }
  }

  submitNewPlan() {
    this.stepper.selected.completed = true;
    this.loaderService.setLoading(true);
    this.planService.submitPlan(this.plan).subscribe({
      next: (r) => {
        this.sharedService.updatePlan(r);
        this.sharedService.updateSubmittedPlan(r);
        if(r.FinalReflectionStatus === 'Available')
          this.sharedService.updatePlanUnderReflection(r.Id);
        this.planEditService.setIsDirty(false);
        setTimeout(() => {
          this.router.navigateByUrl('app/dashboard');
          this.loaderService.setLoading(false);
          this.dialog.open(DialogSubmitPlanComponent, {
            width: '1000px',
            height: 'auto',
            maxHeight: '80vh',
            panelClass: 'submit-plan',
            backdropClass: 'bdrop',
            position: { top: '100px' },
            data: { userId: this.plan.UserId, isEditing: false },
            ariaLabel: 'plan submission status',
            disableClose: true
          });
        }, 3000);
      },
      error: () => {
        this.loaderService.setLoading(false);
        this.openSnackbarError("There was an error submitting your CPD Plan because there is incomplete information. \n Please refresh the page, return to Step 3 and ensure all required fields are completed in each step before resubmitting.");
      }
    });
  }

  submitEditPlan() {
    this.stepper.selected.completed = true;
    this.loaderService.setLoading(true);
    this.planService.closeEditPlan(this.plan).subscribe({
      next: (r) => {
        this.sharedService.updateSubmittedPlan(r);
        this.sharedService.updatePlan(r);
        this.planEditService.setIsDirty(false);
        this.planEditService.setIsEditing(false, 1, null);
        setTimeout(() => {
          this.router.navigateByUrl('app/dashboard');
          this.loaderService.setLoading(false);
          this.dialog.open(DialogSubmitPlanComponent, {
            width: '875px',
            height: 'auto',
            maxHeight: '80vh',
            panelClass: 'submit-plan',
            backdropClass: 'bdrop',
            position: { top: '100px' },
            data: { userId: this.plan.UserId, isEditing: true },
            ariaLabel: 'plan submission status',
            disableClose: true
          });
        }, 3000);
      },
      error: () => {
        this.loaderService.setLoading(false);
        this.openSnackbarError("Your CPD self-assessment was not submitted. Please try again.");
      }
    });
  }

  stepChanged(event: StepperSelectionEvent) {
    if (event.selectedIndex < event.previouslySelectedIndex) {
      for (let i = 0; i < this.stepper.steps.length - 1; i++) {
        if (event.selectedIndex <= i)
          this.stepper.steps.get(i).completed = false;
      }
      if (event.previouslySelectedIndex > event.selectedIndex) {
        event.previouslySelectedStep.interacted = false;
      }
    }
    this.clearErrors();
    this.pageLandingAnnouncement(event.selectedIndex+1);
    const newStep = {...this.stepStatus};
    newStep.Step = event.selectedIndex + 1;
    this.stepStatusService.updateStep(newStep).subscribe();
  }

  step() {
    this.stepper.selected.completed = true;
    this.stepper.next();
    document.getElementsByTagName('mat-sidenav-content')[0]?.scrollTo(0, 0);
    this.isButtonLoading = false;
  }

  pageLandingAnnouncement(step: number): string{
      switch (step){
        case 1:
          this.landingAnnouncementString = 'Step 1: Introduction → Introduction';
          break
        case 2:
          this.landingAnnouncementString = 'Step 2: PD Goals → Reflect on your professional development goals';
          break
        case 3:
          this.landingAnnouncementString = 'Step 3: Select Competencies → Select competencies to develop or enhance';
          break
        case 4:
          this.landingAnnouncementString = 'Step 4: Self-Assessment → Self-assess your proficiency in your selected competencies';
           break
        case 5:
          this.landingAnnouncementString = 'Step 5: Set Priorities → Set your priorities';
          break
        case 6:
          this.landingAnnouncementString = 'Step 6: Set Learning Activities → Set your learning activities';
          break
        case 7:
          this.landingAnnouncementString = 'Step 7: Review and Submit → Review and submit your CPD plan';
          break
    }
    return this.landingAnnouncementString;
  }

  getTooltip(index) {
    switch (index) {
      case 0:
        this.mouseOverTooltip = 'Introduction';
        break
      case 1:
        this.mouseOverTooltip = 'PD Goals';
        break
      case 2:
        this.mouseOverTooltip = 'Select Competencies';
        break
      case 3:
        this.mouseOverTooltip = 'Self-Assessment';
        break
      case 4:
        this.mouseOverTooltip = 'Set Priorities';
        break
      case 5:
        this.mouseOverTooltip = 'Set Learning Activities';
        break
      case 6:
        this.mouseOverTooltip = 'Review and Submit';
        break
    }
  }

  clearErrors() {
    if(this.setPriorities){
      this.setPriorities.showMaxCompetencyError = false;
      this.setPriorities.showMinCompetencyError = false;
    }
    if(this.setLearning){
      this.setLearning.showErrorBar = false;
      this.setLearning.notesLengthError = false;
      this.setLearning.otherActicitiesLengthError = false;
      this.setLearning.showMaxCompetencyError = false;
      this.setLearning.showMinCompetencyError = false;
    }
    if(this.selfAssessment){
      this.selfAssessment.showError = false;
    }
  }
}
